import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AppTooltip, Checkbox, Loader } from '@igo/ui';
import { LogNotification, LogNotifyType } from '@/api';
import { useAuth } from '@/app/AuthHook';
import { useAudio } from '@/app/AudioProvider';
import { useIsMobile, useLocalStorage } from '@/shared/utils';
import { Skeleton } from '@/shared/ui/skeleton/Skeleton';
import { Notification as ToastNotification } from '@/shared/ui/notifications/notifications';
import { getNotificationMessage } from '../model/getNotificationMessage';
import { useNotifications } from '../model/useNotifications';
import BellIcon from './assets/bell.svg?react';
import SettingsIcon from './assets/settings.svg?react';
import NotificationSound from './assets/notification.wav';
import { NotificationGroup } from './NotificationGroup';
import { useGameStore } from '@/entities/game-board/model/server-game/store';
import { shouldSilenceDonationNotification } from '../model/shouldSilenceDonationNotification';
import styles from './styles.module.scss';

const NOTIFICATION_SOUND_VOLUME = 0.2;

const getNotificationLink = (notification: LogNotification) => {
    const linkId = notification.messageData?.link_id;

    if (notification.type === LogNotifyType.NOTIFY_TYPE_BROADCAST) {
        return linkId ? `/broadcast/${linkId}` : '/broadcasts';
    }
    if (notification.type === LogNotifyType.NOTIFY_TYPE_GAME) {
        return linkId ? `/server/${linkId}` : '/server';
    }
    if (notification.type === LogNotifyType.NOTIFY_TYPE_TOURNAMENT && notification.parentId) {
        return `/tournaments/${notification.parentId}`;
    }
};

export const NotificationsCenter = () => {
    const { user } = useAuth();
    const enabled = !!user?.ID;
    const [isOpen, setIsOpen] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isSoundEnabled, setIsSoundEnabled] = useLocalStorage('notification-sound-enabled', true);
    const isMobile = useIsMobile(900);
    const { playSoundFile } = useAudio();
    const rootRef = useRef<HTMLDivElement>(null);
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;
    const navigate = useNavigate();
    const routeBroadcastId = useMatch('/broadcast/:id')?.params.id;
    const gameId = useGameStore((store) => store.gameInfo.gameId);
    const currentUserId = useGameStore((store) => store.gameInfo.userId);
    const broadcasterId = useGameStore((store) => store.game?.broadcaster_info?.id);
    const donationsEnabled = useGameStore((store) =>
        Boolean((store.tree.root.data._DONATIONS as string[] | undefined)?.includes('enabled'))
    );
    const { t } = useTranslation('global', { keyPrefix: 'notifications' });
    const {
        notifications,
        unreadCount,
        isLoading,
        isMutating,
        toggleRead,
        markAllAsRead,
        deleteNotification,
        deleteAll,
    } = useNotifications(enabled, (notification) => {
        const silentDonation = shouldSilenceDonationNotification(notification, {
            routeBroadcastId,
            gameId,
            broadcasterId,
            currentUserId,
            donationsEnabled,
        });

        if (!silentDonation && isSoundEnabled) playSoundFile(NotificationSound, NOTIFICATION_SOUND_VOLUME);

        if (!silentDonation && !isOpenRef.current) {
            toast(<ToastNotification type="info" text={getNotificationMessage(notification, t)} />, {
                style: { border: '1px solid #0d99ff' },
            });
        }
    });

    const { unread, read } = useMemo(
        () => ({
            unread: notifications.filter((notification) => !notification.readAt),
            read: notifications.filter((notification) => !!notification.readAt),
        }),
        [notifications]
    );

    useEffect(() => {
        if (!isOpen) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const handlePointerDown = (event: PointerEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) setIsOpen(false);
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsOpen(false);
        };

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!enabled) {
            setIsOpen(false);
            setIsSettingsOpen(false);
        }
    }, [enabled]);

    if (!enabled) return null;

    const openNotification = async (notification: LogNotification) => {
        if (!notification.readAt && notification.id) await toggleRead(notification.id);
        const link = getNotificationLink(notification);
        if (link) navigate(link);
        setIsOpen(false);
    };

    return (
        <div className={styles.center} ref={rootRef}>
            <button
                type="button"
                className={styles.trigger}
                aria-label={t('title')}
                aria-expanded={isOpen}
                onClick={() => {
                    setIsOpen((current) => !current);
                    setIsSettingsOpen(false);
                }}
            >
                <BellIcon />
                {unreadCount > 0 && <span className={styles.badge}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
            </button>

            {isOpen && (
                <div className={styles.panel}>
                    <div className={styles.panelHeader}>
                        <h2>{t('title')}</h2>
                        <div className={styles.headerActions}>
                            <button
                                type="button"
                                disabled={unread.length === 0 || isMutating}
                                onClick={() => void markAllAsRead()}
                            >
                                {t('markAllAsRead')}
                            </button>
                            <span className={styles.headerDivider} />
                            <button
                                type="button"
                                disabled={notifications.length === 0 || isMutating}
                                onClick={() => void deleteAll()}
                            >
                                {t('deleteAll')}
                            </button>
                            <button
                                type="button"
                                className={styles.settingsButton}
                                aria-label={t('settings')}
                                aria-expanded={isSettingsOpen}
                                data-tooltip-id={!isMobile ? 'notification-center-tooltip' : undefined}
                                data-tooltip-content={!isMobile ? t('settings') : undefined}
                                onClick={() => setIsSettingsOpen((current) => !current)}
                            >
                                <SettingsIcon />
                            </button>
                            {isSettingsOpen && (
                                <div className={styles.settingsPopover}>
                                    <Checkbox
                                        label={t('sound')}
                                        checked={isSoundEnabled}
                                        onChange={(enabled) => {
                                            setIsSoundEnabled(enabled);
                                            if (enabled) playSoundFile(NotificationSound, NOTIFICATION_SOUND_VOLUME);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={styles.list}>
                        {isLoading ? (
                            <div className={styles.loading}>
                                {Array.from({ length: 4 }, (_, index) => (
                                    <div key={index} className={styles.loadingItem}>
                                        <Skeleton className={styles.iconSkeleton} />
                                        <div>
                                            <Skeleton className={styles.titleSkeleton} />
                                            <Skeleton className={styles.messageSkeleton} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className={styles.empty}>{t('empty')}</div>
                        ) : (
                            <>
                                <NotificationGroup
                                    title={t('unread')}
                                    items={unread}
                                    onOpen={(notification) => void openNotification(notification)}
                                    onToggleRead={(id) => void toggleRead(id)}
                                    onDelete={(id) => void deleteNotification(id)}
                                    onChallengeAccepted={(gameId) => {
                                        setIsOpen(false);
                                        navigate(`/server/${gameId}`);
                                    }}
                                    tooltipsEnabled={!isMobile}
                                />
                                <NotificationGroup
                                    title={t('read')}
                                    items={read}
                                    onOpen={(notification) => void openNotification(notification)}
                                    onToggleRead={(id) => void toggleRead(id)}
                                    onDelete={(id) => void deleteNotification(id)}
                                    onChallengeAccepted={(gameId) => {
                                        setIsOpen(false);
                                        navigate(`/server/${gameId}`);
                                    }}
                                    tooltipsEnabled={!isMobile}
                                />
                            </>
                        )}
                    </div>
                    {isMutating && (
                        <div className={styles.mutating}>
                            <Loader />
                        </div>
                    )}
                </div>
            )}
            <AppTooltip
                id="notification-center-tooltip"
                place="top"
                positionStrategy="fixed"
                delayShow={300}
                className={styles.notificationTooltip}
            />
        </div>
    );
};
